export const random = (min: number = 0, max: number = 1) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const range = (start: number, stop: number, step = 1) => {
  const isReversed: boolean = stop < start;
  [start, stop] = [start, stop].sort();
  const result = Array(Math.ceil((stop - start + 1) / step))
    .fill(start)
    .map((x, y) => x + y * step);
  return isReversed ? result.reverse() : result;
};

export const toFloorFixed = (number: number, fixed: number = 2) => {
  const re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?');
  return number?.toString().match(re)![0];
};

export const parseToNumber = (value: number | string): number | undefined => {
  const numValue = Number(value);
  return isNaN(numValue) ? undefined : numValue;
};

export function isValidNumber(value: any): boolean {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}

// TODO: add unit test
export const truncateDecimal = (
  num: string | number,
  decimalPlaces: number
): string => {
  const stringedNum = typeof num === 'number' ? num.toString() : num;

  const parts = stringedNum.split('.');

  if (parts.length === 1) {
    return stringedNum; // No decimal part, just return the string
  }
  const [integerPart] = parts;
  const decimalPart = parts[1].substring(0, decimalPlaces); // Get the decimal part up to the specified length

  return `${integerPart}.${decimalPart}`;
};

export const parseNumber = (
  value: string | number,
  fractionDigits: number = 3
): string => {
  const stringValue: string = value.toString();
  const isNegative: boolean = stringValue.startsWith('-');

  let [mainPart = '', floatPart = ''] = stringValue
    .split('.')
    .map((s: string) => s.replace(/\D/g, ''));

  floatPart =
    floatPart && fractionDigits
      ? Number(`0.${floatPart}`).toFixed(fractionDigits).replace('0.', '')
      : '';

  const parsedNumber: string = `${isNegative ? '-' : ''}${mainPart}${
    floatPart ? `.${floatPart}` : ''
  }`;

  return isNaN(+parsedNumber) ? '' : parsedNumber;
};

export const numberFormat = (
  value: string | number,
  fractionDigits?: number
): string =>
  [0, '0'].includes(value)
    ? '0'
    : value
      ? Intl.NumberFormat('en-US').format(+parseNumber(value, fractionDigits))
      : '';

export const numberWithSign = (
  value: string | number,
  fractionDigits?: number
): string => {
  const parsedNumber: string = parseNumber(value, fractionDigits);
  return +parsedNumber > 0 ? `+${parsedNumber}` : parsedNumber;
};

export const numberFormatWithSign = (
  value: number | string,
  fractionDigits?: number
) => {
  const parsedNumber: string = parseNumber(value, fractionDigits);
  const formattedNumber: string = numberFormat(value, fractionDigits);
  return +parsedNumber > 0 ? `+${formattedNumber}` : formattedNumber;
};

export const toEnglishNumber = (value: string | number): string =>
  `${value}`
    .replace(/۰|٠/g, '0')
    .replace(/۱|١/g, '1')
    .replace(/۲|٢/g, '2')
    .replace(/۳|٣/g, '3')
    .replace(/۴|٤/g, '4')
    .replace(/۵|٥/g, '5')
    .replace(/۶|٦/g, '6')
    .replace(/۷|٧/g, '7')
    .replace(/۸|٨/g, '8')
    .replace(/۹|٩/g, '9');

export const toPersianNumber = (value: string | number): string =>
  `${value}`
    .replace(/0|٠/g, '۰')
    .replace(/1|١/g, '۱')
    .replace(/2|٢/g, '۲')
    .replace(/3|٣/g, '۳')
    .replace(/4|٤/g, '۴')
    .replace(/5|٥/g, '۵')
    .replace(/6|٦/g, '۶')
    .replace(/7|٧/g, '۷')
    .replace(/8|٨/g, '۸')
    .replace(/9|٩/g, '۹');

export const formatNumberWithPadding = (value?: number): string => {
  if (!value && value !== 0) return '';
  return Number.isInteger(value)
    ? `\u00A0`.repeat(8) + `${value}`
    : `${value.toFixed(2)}`;
};

export const toPersianOrdinal = (num: number): string => {
  const ordinals: { [key: number]: string } = {
    0: 'صفرم',
    1: 'اول',
    2: 'دوم',
    3: 'سوم',
    4: 'چهارم',
    5: 'پنجم',
    6: 'ششم',
    7: 'هفتم',
    8: 'هشتم',
    9: 'نهم',
    10: 'دهم',
    11: 'یازدهم',
    12: 'دوازدهم',
    13: 'سیزدهم',
    14: 'چهاردهم',
    15: 'پانزدهم',
    16: 'شانزدهم',
    17: 'هفدهم',
    18: 'هجدهم',
    19: 'نوزدهم',
    20: 'بیستم',
  };

  return ordinals[num] || `${num}م`; // Default case for numbers beyond 20
};

export const roundToNearest = (num: number, unit: number): number => {
  return Math.round(num / unit) * unit;
};

export const calculateGrowthPercentage = (
  newValue: number,
  oldValue: number
): string => {
  if (oldValue === 0) {
    throw new Error('Old value cannot be zero to avoid division by zero.');
  }
  const growth = ((newValue - oldValue) / oldValue) * 100;
  return growth.toFixed(2);
};
